/* Note to future people
 * put the django urlname next to the URLs (not the obvious REST ones)
 * Then when refactoring, we don't miss out */

import {NOTIFICATIONS_PAGE_URL} from 'js-libs/sweetcommons/urls';

import {ActionableDocumentType} from 'app/general/types';
import {getLocation, getPathFromURL} from 'app/utils/urls';

import {
  BasicTeamSerializedType,
  DetailPolicySerializedType,
  ProcedureHashidSerializedType,
  ProcedureSerializedType,
  ProcessSerializedType,
  QuizSerializedType,
} from 'sweet-types';

import type {VersionedObject} from 'app/version/types';

export const SIGNUP_PAGE_URL = '/accounts/signup/';
export const RECYCLE_BIN_URL = '/accounts/recycle/';

export const TOUR_API_URL = '/~api/tour/'; // api_tutorial
export const APP_SETTINGS_URL = '/api/v1/settings/';
export const APP_SETTINGS_TIMEZONES_URL = '/api/v1/settings/timezones/';
export const APP_SETTINGS_LANGUAGES_URL = '/api/v1/settings/languages/';

export const IMPORTDOC_API_URL = '/api/v1/docimport/';

export const ACCEPTANCE_API_URL = '/api/v1/acceptance/';
export const ACCEPTANCE_STATUS_API_URL = '/api/v1/acceptancestatus/';

export const ACTIVITY_API_URL = '/api/v1/activities/';
export const PERMISSIONS_API_URL = '/api/v1/permissions/';
export const PERMISSIONS_EXPORT_API_URL = '/api/v1/permissions/export/';
export const INVITATION_API_URL = '/api/v1/invitations/';
export const INVITATION_API_RESEND_URL = '/api/v1/invitations/resend/';
export const DOCUMENTS_API_URL = '/api/v1/documents/';
export const FOLDERS_API_URL = '/api/v1/folders/';
export const FOLDERS_HASHID_API_URL = '/api/v1/foldersdetail/';
export const getFolderURLByHashid = (hashid: string): string =>
  `${FOLDERS_HASHID_API_URL}${hashid}/`;
export const PROCEDURES_API_URL = '/api/v1/procedures/';
export const PROCEDURES_HASHID_API_URL = '/api/v1/proceduresdetail/';
export const PROCEDURES_API_URL_COPY = '/api/v1/procedures/copy/';
export const PROCEDURES_VERSIONS_API_URL = '/api/v1/procedureversions/';
export const PROCEDURES_VERSION_RESTORE_API_URL =
  '/api/v1/procedureversions/restore/';
export const PROCEDURES_VERSION_APPROVE_API_URL =
  '/api/v1/procedureversions/approve/';
export const PROCEDURE_VERSION_SIGNOFFREQUEST_API_URL =
  '/api/v1/procedureversions/signoffrequest/';
export const REPORTS_API_URL = '/api/v1/reports/';
export const REPORTSRUN_API_URL = '/api/v1/reportruns/';
export const REPORTSRUN_CREATE_API_URL = `${REPORTSRUN_API_URL}run/`;
export const REVIEW_API_URL = '/api/v1/reviews/';

export const TAGS_DASHBOARD_URL = '/tags/';
export const TAGS_QRCODE_URL = '/tags/qrcodes/';
export const TAGS_API_URL = '/api/v1/tags/';
export const TAGGEDITEMS_API_URL = '/api/v1/taggeditems/';

export const getProcedureDetailURLByHashid = (hashid: string): string => {
  return `${PROCEDURES_HASHID_API_URL}${hashid}/`;
};
export const IMAGE_ERROR_PNG = '/site_media/static/images/cannotload-1.png';
export const VIDEO_ERROR_PNG = '/site_media/static/images/cannotload-2.png';
export const MEDIAACCESS_SECURE_SIGNER_API_URL = '/api/v1/media-access/';

export const COMMENTS_API = '/api/v1/comments/';
export const NOTIFICATIONS_API = '/api/v1/notifications/';
export const NOTIFICATIONS_MARK_READ = '/api/v1/notifications/mark-all-read/';

export const OPENAI_SUGGESTIONS_API_URL = '/api/v1/suggestions/';

export const POLICIES_DASHBOARD_URL = '/policy/';
export const POLICIES_NEW_POLICY = '/policy/new/';
export const POLICIES_API = '/api/v1/policy/';
export const POLICIES_API_URL_COPY = `${POLICIES_API}copy/`;
export const POLICIES_VERSIONS_API = '/api/v1/policyversion/';
export const POLICIES_VERSIONS_APPROVE_API_URL =
  '/api/v1/policyversion/approve/';
export const POLICY_VERSION_SIGNOFFREQUEST_API_URL =
  '/api/v1/policyversion/signoffrequest/';
export const POLICY_PROCEDURE_ATTACH_API = '/api/v1/policyprocedure/';
export const POLICY_PROCESS_ATTACH_API = '/api/v1/policyprocess/';
export const POLICY_HASHID_API_URL = '/api/v1/policydetail/';
export const getPolicyProceduresURL = (
  policy: DetailPolicySerializedType,
  search = ''
): string =>
  getPathFromURL(appendPathToURL(policy.html_url, `procedures/${search}`));
export const getPolicyDetailURLByHashid = (hashid: string): string => {
  return `${POLICY_HASHID_API_URL}${hashid}/`;
};

export const getQuizProceduresURL = (quiz: QuizSerializedType): string =>
  getPathFromURL(appendPathToURL(quiz.html_url, 'procedures/'));
export const getQuizProcessesURL = (quiz: QuizSerializedType): string =>
  getPathFromURL(appendPathToURL(quiz.html_url, 'processes/'));
export const getQuizPoliciesURL = (quiz: QuizSerializedType): string =>
  getPathFromURL(appendPathToURL(quiz.html_url, 'policies/'));
export const getQuizAttemptsURL = (quiz: QuizSerializedType): string =>
  getPathFromURL(appendPathToURL(quiz.html_url, 'attempts/'));

export const getTaskReportURL = (html_url: string, reportID): string =>
  getPathFromURL(appendPathToURL(html_url, `tasks/report/${reportID}/`));

export const VIDEO_API = '/api/v1/video/';
export const VIDEO_DETAILS = '/video/';

export const TEAMS_API_URL = '/api/v2/teams/';
export const TEAM_PROCEDURES_API_URL = '/api/v1/teamprocedures/';
export const TEAM_PROCESSES_API_URL = '/api/v1/teamprocesses/';
export const TEAM_POLICIES_API_URL = '/api/v1/teampolicies/';
export const TEAM_FOLDERS_API_URL = '/api/v1/teamfolders/';
export const TEAM_QUIZZES_API_URL = '/api/v1/teamquizzes/';
export const TEAM_ROLES_API_URL = '/api/v1/roles/';
export const TEAM_ROLE_MEMBERSHIPS_API_URL = '/api/v1/rolememberships/';
export const TEAM_USERS_API_URL = '/api/v1/teamusers/';

export const PROCESS_DASHBOARD_URL = '/groups/';
export const PROCESS_API_URL = '/api/v2/processes/';
export const PROCESS_HASHID_API_URL = '/api/v2/processesdetail/';
export const PROCESS_CREATE_PAGE_URL = '/groups/add/'; // group-add
export const PROCESS_API_URL_COPY = '/api/v2/processes/copy/';
export const PROCESS_VERSIONS_API = '/api/v1/processversions/';
export const PROCESS_VERSIONS_MEMBERSHIP_API =
  '/api/v1/processversionsmemberships/';
export const PROCESS_VERSION_APPROVE_API_URL =
  '/api/v1/processversions/approve/';
export const PROCESS_VERSION_SIGNOFFREQUEST_API_URL =
  '/api/v1/processversions/signoffrequest/';
export const PROCESS_VERSIONS_MEMBERSHIP_CREATE_API = `${PROCESS_VERSIONS_MEMBERSHIP_API}create-and-associate/`;
export const getProcessVersionAddNewProcedureURL = (
  versionURL: string
): string => `${versionURL}create-and-associate-procedure/`;

export const PRODUCTBUNDLE_API_URL = '/api/v1/product/';
export const TEMPLATES_API_URL = '/api/v1/template/';
export const TEMPLATES_DASHBOARD_URL = '/market/templates/';
export const getTemplateDetailURLByHashid = (hashid: string): string => {
  return `${TEMPLATES_API_URL}${hashid}/`;
};

export const USER_API_URL = '/api/v1/users/'; // user-list
export const USER_SET_READONLY_API_URL = '/api/v1/users/readonly/'; // user-readonly
export const USERDATA_API_URL = '/api/v1/user/'; // current-user-list
export const USERDATA_ACCOUNTS_API_URL = '/api/v1/user/accounts/'; // current-user-accounts
export const USERDATA_SWITCHACCOUNT_API_URL = '/api/v1/user/switch-accounts/'; // current-user-switch-account
export const USERDATA_SUDO_API_URL = '/api/v1/user/sudo/'; // current-user-sudo
export const USERDATA_SOCIAL_AUTH_API_URL = '/api/v1/user/social/';
export const SOCIAL_AUTH_BEGIN_URL = '/accounts/login/'; // social:begin
export const SOCIAL_AUTH_DISCONNECT_URL = '/accounts/disconnect/'; // social:disconnect
export const EMAIL_API_URL = '/api/v1/email/'; // email-list
export function getEmailResendConfirmationAPIURL(id: number) {
  return `/api/v1/email/${id}/resend-confirmation/`;
}

export const ACCOUNT_OVERVIEW_URL = '/accounts/overview/'; // overview:index
export const ACCOUNT_OVERVIEW_BULK_USER_SPREADSHEET_URL =
  '/accounts/b/example.xls'; // bulk:example
export const ACCOUNT_OVERVIEW_BULK_USER_UPLOAD_URL = '/accounts/b/csv/'; // bulk:upload
export const ACCOUNT_EMAIL_URL = '/accounts/emailsettings/'; // overview:emailsettings
export const ACCOUNT_CREATE_URL = '/accounts/createaccount/'; // overview:createaccount
export const ACCOUNT_SWITCH_URL = '/accounts/switch/';
export const ACCOUNT_PASSWORD_CHANGE_URL = '/accounts/password_change/'; // password_change
export const ACCOUNT_TOKENS_URL = '/accounts/tokens/'; // tokens:index
export const ACCOUNT_WEBHOOK_URL = '/accounts/webhooks/'; // webhooks:index
export const ACCOUNT_REPORT_URL = '/accounts/reports/'; // reports:index
export const ACCOUNT_SCIM_URL = '/accounts/scim/'; // scim:index
export const ACCOUNT_REFERRALS_URL = '/accounts/referrer/'; // referrer:index
export const RECYCLED_RESOURCE_API_URL = '/api/v1/recycle/';
export const ACCOUNT_REFERRALS_FORM_URL = '/referral-program/'; // referral-program
export const ACCOUNT_MIGRATION_URL = '/accounts/migration/'; // overview:migration
export const ACCOUNT_EXPORT_PDF = '/accounts/export/pdf/'; // export:bulkpdf
export const ACCOUNT_2FA = '/accounts/two-factor-auth/';
export const FORCE_2FA = '/accounts/two-factor-auth/setup/';
export const LOGIN_URL = '/accounts/login/';
export const LOGOUT_URL = '/accounts/logout/'; // logout
export const TOKENS_API_URL = '/api/v1/tokens/';
export const SCOPEDTOKENS_API_URL = '/api/v1/scopedtokens/';
export const WEBHOOKS_API_URL = '/api/v1/hook/';
export const LOGS_API_URL = '/api/v1/log/';
export const SCIMTEAM_API_URL = '/api/v1/scimteams/';
export const REFERRALS_CODES_API_URL = '/api/v1/webinarcodes/';
export const REFERRALS_REFERRALS_API_URL = '/api/v1/referrals/';

export const HOME_REDIRECT = '/home/';
export const HOME_FOLDERS_DASHBOARD = '/home/'; // dash:index
export const HOME_TODOS_DASHBOARD = '/home/todos/'; // dash:todos
export const HOME_WELCOME_TEAMS = '/documents/import/';
export const HOME_WELCOME_IMPORT = '/documents/welcome/';
export const HOME_WELCOME_INVITATIONS = '/documents/invite/';
export const PROCEDURES_ADD_NEW = '/procedures/new/'; // procedure-new
export const PROCEDURES_HOME = '/procedures/'; // home
export function getDashboardFolderDetailURL( // dash:detail
  name: string,
  hashid: string
): string {
  return `${HOME_FOLDERS_DASHBOARD}${encodeURIComponent(name)}/${hashid}/`;
}

export const DOCUMENTS_HOME = '/documents/'; // documents:list
export const DOCUMENT_CREATE_PAGE_URL = '/documents/new/';
export const DOCUMENTS_QRCODE = '/folders/qrcodes/'; // folders:qrcodes
export const FOLDERS_HOME = '/folders/'; // folders:index
export const getDocumentsQRCodeURL = (hashid: string): string =>
  `${DOCUMENTS_QRCODE}${hashid}/`;

export const DIAGRAM_API_URL = '/api/v1/diagrams/'; // diagrams-list

export const SEARCH_PAGE = '/search/?ct=procedure&q=';
export const SEARCH_API_URL = '/api/v1/search/';
export const SEARCH_API_COUNT_URL = '/api/v1/search/count/';

export const TEAMS_HOME = '/people/'; // team:index
export const TEAMS_CREATE_PAGE_URL = `${TEAMS_HOME}add-team/`; // team:add
export const USERS_HOME = `${TEAMS_HOME}users/`;
export const USERS_CREATE_PAGE_URL = `${USERS_HOME}add/`; // user:add
export const USERS_CREATE_GUEST_PAGE_URL = `${USERS_CREATE_PAGE_URL}guest/`; // user:add
export const getTeamSignoffsURL = (teamid: string): string =>
  `${TEAMS_HOME}team/${teamid}/team/signoffs/`;

export const TASKS_HOME = '/tasks/'; // tasks:list
export const TASKS_ALL_PAGE = `${TASKS_HOME}all/`;
export const TASKS_API_URL = '/api/v1/taskinstances/';
export const TASKS_SLIM_API_URL = '/api/v2/taskinstances/';
export const TASKS_MINE_API_URL = `${TASKS_SLIM_API_URL}mine/`;
export const getTaskUpgradeVersionURL = (taskId: number | string): string =>
  `${TASKS_API_URL}${taskId}/upgrade/`;
export const TASKS_TEMPLATES_API_URL = '/api/v1/tasktemplates/';
export const TASKS_REVOKE_TASK_API_URL = `${TASKS_TEMPLATES_API_URL}revoke/`;
export const TASKS_REASSIGN_TASK_API_URL = `${TASKS_TEMPLATES_API_URL}reassign/`;
export const TASKS_CREATE_TRIGGER_API_URL = `${TASKS_TEMPLATES_API_URL}create-trigger/`;
export const TASKS_MULTI_ASSIGN_API_URL = `${TASKS_TEMPLATES_API_URL}multi-assign/`;
export const TASKS_GUEST_INSTANCE_API_URL = '/api/v1/guesttaskinstances/';
export const AUTOMATION_RUN_API_URL = '/api/v1/automationrun/';

export const THREAD_API_URL = '/api/v1/thread/'; // thread:list
export const SUBSCRIBERS_API_URL = `${THREAD_API_URL}subscribers/`;

export const TEST_ALTERNATIVES_API = '/~api/get-test-alternative'; // get_test_alternative

export const RESET_URL = LOGIN_URL;

export const TRIAL_UPGRADE_URL = '/procedures/trial-expired/';
export const NO_ACCOUNT_URL = '/accounts/empty/';

export const UPGRADE = '/accounts/billing/subscribe/'; // billing:subscribe

export const BILLING_NEW_URL = '/accounts/stripe/new/'; // stripe:new
export const BILLING_DETAIL_URL = '/accounts/stripe/detail/'; // stripe:detail
export const BILLING_REPORT_URL = '/accounts/stripe/report/'; // stripe:report

export const BILLING_PLAN_API_URL = '/api/v1/plans/';

export {NOTIFICATIONS_PAGE_URL};

export const STATUSES = {
  UPGRADE_REQUIRED: 'UPGRADE_REQUIRED',
};

export const QUIZZES_HOME = '/quiz/';
export const QUIZ_CREATE_PAGE_URL = '/quiz/new/';

export const SIGNOFF_API_URL = '/api/v1/signoffs/';
export const SIGNOFF_REMIND_API_URL = '/api/v1/signoffs/remind/';
export const GROUPEDSIGNOFF_API_URL = '/api/v1/groupedsignoffs/';
export const SIGNOFFTEMPLATE_API_URL = '/api/v1/signofftemplates/';
export const SIGNOFF_DOCUMENT_API_URL = '/api/v1/documentsignoffs/';

export const LANES_API = '/api/v1/lanes/';

export const PRINT_API = '/api/v1/print/';
export const EXPORT_ACTIVITY_API_URL = '/api/v1/export/activity/';

export const REACTIONS_API = '/api/v1/reactions/';
export const OWNERSHIP_TRANSFER_API = '/api/v1/ownershiptransfer/';

// knowledge
export const HELP_URL = '/help/';
export const SUPPORT_SEARCH_URL =
  '/help/questions/36/how-do-i-search-for-a-specific-procedure/';
export const SUPPORT_REMINDER_URL =
  '/help/questions/78/schedule-a-periodic-review-for-a-procedure-policy-or-process-so-that-i-can-verify-it-is-up-to-date/';
export const SUPPORT_APPROVAL_URL =
  '/help/questions/35/how-do-i-approve-a-draft-of-a-procedure-so-that-it-becomes-the-live-version-and-notify-everyone-who-has-access-to-it/';

export const KB_API_URL = '/api/v1/kbs/';
export const KB_API_URL_COPY = '/api/v1/kbs/copy/';
export const KB_CATEGORIES_API_URL = '/api/v1/kbcategories/';
export const KB_ARTICLES_API_URL = '/api/v1/kbarticles/';
export const KB_FEEDBACK_API_URL = '/api/v1/kbarticlefeedback/';
export const KB_THEMES_API_URL = '/api/v1/kbtheme/';
export const KB_BLOCKS_API_URL = '/api/v1/kbblock/';
export const KB_DASHBOARD_URL = '/accounts/kb/';
export const KB_NEW_KB = `${KB_DASHBOARD_URL}new/`;
export const getKBEditPage = (slug: string): string =>
  `${KB_DASHBOARD_URL}edit/${slug}/`;

export const ACM_API_URL = '/api/v1/acmcertificate/';

export const QUIZ_API_URL = '/api/v1/quiz/';
export const QUIZ_API_URL_COPY = '/api/v1/quiz/copy/';
export const QUIZ_LINK_API_URL = '/api/v1/quizlink/';
export const QUIZ_VERSION_API_URL = '/api/v1/quizversion/';
export const QUIZ_VERSION_APPROVE_API_URL = '/api/v1/quizversion/approve/';
export const QUIZ_ATTEMPT_API_URL = '/api/v1/attempt/';
export const QUIZ_ATTEMPT_ANSWER_API_URL = '/api/v1/attemptanswer/';
export const QUIZ_COMPLETE_ATTEMPT_API_URL = '/api/v1/attempt/complete/';

export const QRCODE_API_URL = '/api/v1/qrcodes/';

export const ATTACHMENT_API_URL = '/api/v1/attachment/';

export const procedure_process_task_url = ({
  object,
  process,
  task,
}: {
  object: {
    html_url: string;
  };
  process: {
    hashid: string;
  };
  task: {
    id: number;
  };
}): string => {
  const html_url = object.html_url.replace(/\?.*/, '');
  return `${html_url}task/${task.id}/process/${process.hashid}/`;
};

export const getTeamDetailPageURL = (
  id: number | string,
  slug: string,
  objType = 'users'
): string => `${TEAMS_HOME}team/${id}/${slug}/${objType}/`;

export const getTeamAPIURL = (id: string | number): string =>
  `${TEAMS_API_URL}${id}/`;

export const getUserDetailPageURL = (
  id: number | string,
  objType = 'invites'
): string => `${TEAMS_HOME}users/${id}/${objType}/`;

export const appendPathToURL = (url: string, path: string): string => {
  const link = getLocation(url);
  return `${link.pathname}${path}${link.search}`;
};

export const getObjectCurrentVersionURL = (
  procedureOrPolicy?: VersionedObject,
  currentVersionId?: number | string | null,
  search = '',
  htmlURL = ''
): string => {
  const baseurl =
    procedureOrPolicy != null ? procedureOrPolicy.html_url : htmlURL;

  if (currentVersionId !== undefined) {
    return appendPathToURL(
      baseurl,
      'version/' + currentVersionId + '/' + search
    );
  } else if (procedureOrPolicy != null && procedureOrPolicy.current_version) {
    return procedureOrPolicy.current_version.html_url + search;
  } else {
    return appendPathToURL(baseurl, 'version/current/' + search);
  }
};

export const getObjectCommentsURL = (
  document?: VersionedObject,
  currentVersionId?: number | string,
  search = '',
  htmlURL = ''
): string =>
  getPathFromURL(
    appendPathToURL(
      getObjectCurrentVersionURL(document, currentVersionId, search, htmlURL),
      'comment/' + search
    )
  );

export const getObjectAcceptURL = (
  document?: VersionedObject,
  currentVersionId?: number | string,
  search = '',
  versionURL = ''
): string =>
  getPathFromURL(
    appendPathToURL(
      document != null
        ? getObjectCurrentVersionURL(document, currentVersionId)
        : versionURL,
      'accept/' + search
    )
  );

export const getProcessCommentsURL = (
  process: ProcessSerializedType,
  currentVersionId?: string,
  search = ''
): string => {
  const versionId =
    currentVersionId != null ? currentVersionId : process.current_version.id;
  return getPathFromURL(
    `${process.html_url}version/${versionId}/comment/${search}`
  );
};

export const getSignoffUrlForTeam = (team: {html_url: string}): string =>
  getPathFromURL(
    appendPathToURL(team.html_url.replace('users/', ''), 'signoffs/')
  );

export const getProceduresUrlForTeam = (
  team: BasicTeamSerializedType
): string =>
  getPathFromURL(
    appendPathToURL(team.html_url.replace('users/', ''), 'procedures/')
  );

export const getProcessesUrlForTeam = (team: BasicTeamSerializedType): string =>
  getPathFromURL(
    appendPathToURL(team.html_url.replace('users/', ''), 'processes/')
  );

export const getPoliciesUrlForTeam = (team: BasicTeamSerializedType): string =>
  getPathFromURL(
    appendPathToURL(team.html_url.replace('users/', ''), 'policies/')
  );

export const getSignoffUrlForDocument = (
  document: ActionableDocumentType
): string => getPathFromURL(appendPathToURL(document.html_url, 'signoff/'));

export const getObjectTasksURL = (
  object: {
    html_url: string;
  },
  search = ''
): string =>
  getPathFromURL(appendPathToURL(object.html_url, 'tasks/' + search));

export const getObjectActivityURL = (
  object: {
    html_url: string;
  },
  search = ''
): string =>
  getPathFromURL(appendPathToURL(object.html_url, 'activity/' + search));

export const getObjectSignoffURL = (
  object: {
    html_url: string;
  },
  search = ''
): string =>
  getPathFromURL(appendPathToURL(object.html_url, 'signoff/' + search));

export const getObjectReviewURL = (
  object: {
    html_url: string;
  },
  search = ''
): string =>
  getPathFromURL(appendPathToURL(object.html_url, 'review/' + search));

export const getObjectPoliciesURL = (
  object: {
    html_url: string;
  },
  search = ''
): string =>
  getPathFromURL(appendPathToURL(object.html_url, 'policies/' + search));

export const getObjectProcessesURL = (
  object:
    | ProcedureSerializedType
    | ProcedureHashidSerializedType
    | DetailPolicySerializedType,
  search = ''
): string =>
  getPathFromURL(appendPathToURL(object.html_url, 'processes/' + search));

export const getObjectTeamsURL = (
  object: {
    html_url: string;
  },
  search = ''
): string =>
  getPathFromURL(appendPathToURL(object.html_url, 'teams/' + search));

export const getObjectPermissionsURL = (
  object: {
    html_url: string;
  },
  search = ''
): string =>
  getPathFromURL(appendPathToURL(object.html_url, 'permissions/' + search));

export const getLoginURLWithNext = (next?: string): string =>
  `${LOGIN_URL}?next=${
    next != null
      ? next
      : encodeURIComponent(
          window.location.pathname +
            window.location.search +
            window.location.hash
        )
  }`;

export const getTaskUrlForFilters = ({
  completed,
  interval,
  order,
}: {
  completed?: boolean;
  interval?: string;
  order?: string;
}) => {
  const urlArgs: string[] = [];
  if (completed != null) {
    urlArgs.push(`completed=${completed}`);
  }
  if (interval != null) {
    urlArgs.push(`interval=${interval}`);
  }
  if (order != null) {
    urlArgs.push(`ordering=${order}`);
  }
  return `${TASKS_ALL_PAGE}?${urlArgs.join('&')}`;
};

export const attachAssignerToTaskURL = (baseUrl: string, user_url: string) =>
  `${baseUrl}&assigner=${user_url}`;

export const attachUserToTaskURL = (baseUrl: string, user_url: string) =>
  `${baseUrl}&user=${user_url}`;
